import React from "react";
import localforage from "localforage";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import {
  putAutoTopup,
  type NegotiationBalance,
  type PutAutoTopupInput,
} from "../../services/negotiationBalance";
import { isNegotiationBalanceTermsPublished } from "../../services/negotiationBalanceTerms";
import { BannerFeedback } from "../BannerFeedback";
import { Button } from "../Button";
import { formatBRL } from "../financial/InvoiceSplitPreviewModal";
import { AutoTopupSettings, type AutoTopupTermsGate } from "./AutoTopupSettings";
import { BalanceHistory } from "./BalanceHistory";
import {
  formatInstant,
  INELIGIBLE_COPY,
  isResumableTopup,
  pendingTopupLabel,
  RESTRICTED_COPY,
} from "./negotiationBalanceFormat";
import { RechargeFlow } from "./RechargeFlow";
import { TermsAcceptanceFlow } from "./TermsAcceptanceFlow";
import { hasNegotiationAccount, useNegotiationBalance } from "./useNegotiationBalance";
import "./NegotiationBalance.css";

export type NegotiationBalancePanelViewProps = {
  /** The enabled balance after the company opens its account. */
  snapshot: NegotiationBalance;
  /** `org_payments` create, and Payments accepts top-ups. */
  canRecharge: boolean;
  /** The last refresh failed; the amounts are the previous answer. */
  stale?: boolean;
  /** Clock that decides whether `railFrom` is still ahead (epoch ms). */
  nowMs: number;
  onRecharge: () => void;
  /** Shows an open QR again (to pay or cancel it); absent lists the QRs without the action. */
  onResumeTopup?: (id: string) => void;
  /** The weekly automatic top-up settings, when offered. */
  autoTopup?: React.ReactNode;
  /** The ledger, for `org_payments` view. */
  history?: React.ReactNode;
};

/**
 * The Saldo de Negociações on the payments tab, apart from Pix Automático:
 * what is available for bids, what open bids reserve, the balance itself and
 * the top-ups still in confirmation (never counted). Pure presentation:
 * NegotiationBalancePanel owns the requests.
 * @story layout padded
 * @story args { canRecharge: true, nowMs: 1793284200000, snapshot: { rail: "prepaid", account: { state: "active" }, balanceCents: 100000, reservedCents: 15000, availableCents: 85000, pendingTopupCents: 0, pendingTopups: [], terms: { version: "2026-11-negociacoes-v1", acceptedVersion: "2026-11-negociacoes-v1", acceptedAt: "2026-10-28T12:00:00Z", required: false }, topupsEnabled: true, limits: { minTopupCents: 1000, maxTopupCents: 5000000, maxOpenTopups: 3 }, topupFee: { bps: 200, floorCents: 20 }, autoTopup: { available: false, mode: "off", version: 1, eligible: false }, revision: 7 } }
 * @story state InConfirmation { snapshot: { rail: "prepaid", account: { state: "active" }, balanceCents: 100000, reservedCents: 15000, availableCents: 85000, pendingTopupCents: 100000, pendingTopups: [{ id: "t1", channel: "cob", state: "active", netCents: 100000, feeCents: 2000, chargeCents: 102000, expiresAt: "2026-10-29T15:00:00Z" }, { id: "t2", channel: "weekly_line", state: "unknown", netCents: 50000, feeCents: 0, chargeCents: 50000 }], terms: { version: "2026-11-negociacoes-v1", required: false }, topupsEnabled: true, limits: { minTopupCents: 1000, maxTopupCents: 5000000, maxOpenTopups: 3 }, topupFee: { bps: 200, floorCents: 20 }, autoTopup: { available: false, mode: "off", version: 1, eligible: false }, revision: 8 } }
 * @story state Restricted { snapshot: { rail: "prepaid", account: { state: "restricted", restrictedReason: "pix_returned" }, balanceCents: -2000, reservedCents: 0, availableCents: 0, pendingTopupCents: 0, pendingTopups: [], terms: { version: "2026-11-negociacoes-v1", required: false }, topupsEnabled: true, limits: { minTopupCents: 1000, maxTopupCents: 5000000, maxOpenTopups: 3 }, autoTopup: { available: false, mode: "off", version: 1, eligible: false }, revision: 9 } }
 * @story state RailStartsLater { snapshot: { rail: "efi", railFrom: "2026-10-30T03:00:00Z", account: { state: "active" }, balanceCents: 50000, reservedCents: 0, availableCents: 50000, pendingTopupCents: 0, pendingTopups: [], terms: { version: "2026-11-negociacoes-v1", required: false }, topupsEnabled: true, limits: { minTopupCents: 1000, maxTopupCents: 5000000, maxOpenTopups: 3 }, autoTopup: { available: false, mode: "off", version: 1, eligible: false }, revision: 2 } }
 * @story state WithoutPermission { canRecharge: false }
 * @story state Stale { stale: true }
 */
export function NegotiationBalancePanelView({
  snapshot,
  canRecharge,
  stale = false,
  nowMs,
  onRecharge,
  onResumeTopup,
  autoTopup,
  history,
}: NegotiationBalancePanelViewProps) {
  const account = snapshot.account;
  const railFromMs = snapshot.railFrom ? Date.parse(snapshot.railFrom) : NaN;
  const railLater = Number.isFinite(railFromMs) && railFromMs > nowMs;
  const closed = account?.state === "closed";
  return (
    <section
      className="negotiationBalancePanel"
      aria-labelledby="negotiationBalancePanelTitle"
      data-testid="negotiation-balance-panel"
    >
      <header className="negotiationBalancePanelHeader">
        <span className="negotiationBalancePanelIcon" aria-hidden="true">
          <AccountBalanceWalletOutlinedIcon fontSize="small" />
        </span>
        <div>
          <h2 className="negotiationBalancePanelTitle" id="negotiationBalancePanelTitle">
            Saldo de Negociações
          </h2>
          <p className="negotiationBalancePanelSubtitle">Pré-pago, usado só nos lances do Átrio</p>
        </div>
      </header>
      <BannerFeedback
        type="warning"
        open={account?.state === "restricted"}
        message={account?.state === "restricted" ? RESTRICTED_COPY : ""}
      />
      {closed && (
        <p className="negotiationBalanceText">
          O Saldo de Negociações desta empresa foi encerrado.
        </p>
      )}
      {railLater && (
        <p className="negotiationBalanceText" data-testid="negotiation-balance-rail-from">
          Saldo ativo para lances a partir de {formatInstant(snapshot.railFrom)}.
        </p>
      )}
      {!railLater && snapshot.rail !== "prepaid" && !closed && (
        <p className="negotiationBalanceHint" data-testid="negotiation-balance-rail-pending">
          Os lances do Átrio desta empresa ainda são cobrados pelo Pix Automático. O saldo passa a
          valer para lances quando a DaKasa ativar o saldo pré-pago.
        </p>
      )}
      <dl className="negotiationBalanceTotals">
        <div>
          <dt>Disponível para lances</dt>
          <dd data-testid="negotiation-balance-available">{formatBRL(snapshot.availableCents)}</dd>
        </div>
        <div>
          <dt>Reservado em lances</dt>
          <dd data-testid="negotiation-balance-reserved">{formatBRL(snapshot.reservedCents)}</dd>
        </div>
        <div>
          <dt>Saldo total</dt>
          <dd data-testid="negotiation-balance-total">
            {snapshot.balanceCents < 0
              ? `−${formatBRL(Math.abs(snapshot.balanceCents))}`
              : formatBRL(snapshot.balanceCents)}
          </dd>
        </div>
      </dl>
      {(snapshot.pendingTopupCents > 0 || snapshot.pendingTopups.length > 0) && (
        <div className="negotiationBalanceInTransit">
          {/* pendingTopupCents is the sum of the nets of the open cob (QR)
              top-ups only; pendingTopups also lists a weekly line planned on
              a sealed weekly charge, which that sum leaves out. So the header
              names only the Pix recharges, the list shows whenever anything
              is in transit, and each line leads with its own net; the Pix
              total adds the fee. */}
          <p className="negotiationBalanceText" data-testid="negotiation-balance-pending">
            {snapshot.pendingTopupCents > 0 &&
              `Em confirmação ${formatBRL(snapshot.pendingTopupCents)} de recargas por Pix. `}
            Recargas ainda não creditadas não contam no saldo.
          </p>
          {snapshot.pendingTopups.length > 0 && (
            <ul className="negotiationBalancePending">
              {snapshot.pendingTopups.map((topup) => (
                <li key={topup.id}>
                  {topup.channel === "weekly_line" ? "Recarga semanal" : "Recarga por Pix"} de{" "}
                  {formatBRL(topup.netCents)}
                  {topup.feeCents > 0 ? ` (Pix de ${formatBRL(topup.chargeCents)})` : ""} ·{" "}
                  {pendingTopupLabel(topup.state)}
                  {onResumeTopup && isResumableTopup(topup) && (
                    <button
                      type="button"
                      className="negotiationRechargeSecondary"
                      aria-label={`Ver QR da recarga de ${formatBRL(topup.netCents)}`}
                      onClick={() => onResumeTopup(topup.id)}
                    >
                      Ver QR
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {stale && (
        <p className="negotiationBalanceHint" role="status">
          Não foi possível atualizar o saldo agora. Os valores podem estar desatualizados.
        </p>
      )}
      {!closed && (
        <div className="negotiationBalancePanelActions">
          {canRecharge && snapshot.topupsEnabled ? (
            <Button callback={onRecharge}>Recarregar</Button>
          ) : !snapshot.topupsEnabled ? (
            <p className="negotiationBalanceHint">
              As recargas do Saldo de Negociações estão temporariamente indisponíveis.
            </p>
          ) : (
            <p className="negotiationBalanceHint">
              Peça a alguém com permissão de Pagamentos para recarregar.
            </p>
          )}
        </div>
      )}
      {snapshot.terms.acceptedVersion && (
        <p className="negotiationBalanceHint" data-testid="negotiation-balance-terms-accepted">
          Condições aceitas: versão {snapshot.terms.acceptedVersion}
          {snapshot.terms.acceptedAt ? ` em ${formatInstant(snapshot.terms.acceptedAt)}` : ""}.
        </p>
      )}
      <details className="negotiationBalanceExplainer">
        <summary>Como o saldo funciona</summary>
        <p className="negotiationBalanceHint">
          Os lances reservam o valor máximo. Na apuração, são debitados os lances vencedores e os
          patrocínios do público menor habilitados. Reservas superadas voltam em segundos, exceto o
          lance que mantém esse patrocínio.
        </p>
        <p className="negotiationBalanceHint">
          Para veicular no Átrio, é preciso Pix Automático aprovado e cobrança-base semanal
          admitida. Rádio e TV continuam na cobrança semanal.
        </p>
      </details>
      {autoTopup && (
        <div className="negotiationBalanceSection">
          <h3 className="negotiationBalanceSectionTitle">Recarga automática</h3>
          {autoTopup}
        </div>
      )}
      {history && (
        <div className="negotiationBalanceSection">
          <h3 className="negotiationBalanceSectionTitle">Histórico do saldo</h3>
          {history}
        </div>
      )}
    </section>
  );
}

export type NegotiationBalancePanelProps = {
  /** `org_payments` view: the payments page loader already requires it. */
  canView: boolean;
  /** `org_payments` create: recharge and accept the conditions. */
  canCreate: boolean;
  /** `org_payments` edit: the weekly automatic top-up. */
  canEdit: boolean;
  /**
   * The page shows the ledger elsewhere. When given, the panel leaves its
   * history out and reports the balance revision the ledger reloads on, or
   * null while there is no account (or the panel is gone).
   */
  onHistoryKeyChange?: (reloadKey: number | null) => void;
};

function autoTopupFailureCopy(code: string, reason?: keyof typeof INELIGIBLE_COPY): string {
  switch (code) {
    case "negotiation_auto_topup_stale":
      return "A recarga automática mudou em outra sessão. Confira os valores e salve de novo.";
    case "negotiation_auto_topup_ineligible":
      return INELIGIBLE_COPY[reason ?? "unknown"];
    case "negotiation_auto_topup_disabled":
      return "A recarga automática está temporariamente indisponível.";
    case "negotiation_terms_required":
      return INELIGIBLE_COPY.terms_required;
    default:
      return "Não foi possível salvar a recarga automática. Tente de novo em instantes.";
  }
}

/**
 * The Saldo de Negociações section of /organizacao/pagamentos. Server
 * declared: an enabled answer without an account offers the conditions that
 * open it to a payments manager. An account is polled every 15 s while visible.
 * A disabled Payments feature renders nothing.
 * @story skip request lifecycle is covered by CI unit tests; see NegotiationBalancePanelView for every visual state
 */
export function NegotiationBalancePanel({
  canView,
  canCreate,
  canEdit,
  onHistoryKeyChange,
}: NegotiationBalancePanelProps) {
  const balance = useNegotiationBalance({ enabled: canView });
  const historyKey =
    balance.snapshot && hasNegotiationAccount(balance.snapshot) ? balance.snapshot.revision : null;
  React.useEffect(() => {
    onHistoryKeyChange?.(historyKey);
  }, [onHistoryKeyChange, historyKey]);
  React.useEffect(() => () => onHistoryKeyChange?.(null), [onHistoryKeyChange]);
  // Open recharge: a new QR, or one left open earlier shown again.
  const [recharge, setRecharge] = React.useState<{ resumeTopupId?: string } | null>(null);
  // The conditions acceptance opened from the automatic top-up.
  const [termsOpen, setTermsOpen] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [autoError, setAutoError] = React.useState<string>();
  const aliveRef = React.useRef(true);
  React.useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);
  const { refresh } = balance;

  const saveAutoTopup = React.useCallback(
    async (input: PutAutoTopupInput) => {
      setSaving(true);
      setAutoError(undefined);
      let jwt: string | null | undefined;
      try {
        jwt = await localforage.getItem<string>("JWT");
      } catch {
        jwt = undefined;
      }
      const result = jwt ? await putAutoTopup(jwt, input) : undefined;
      if (!aliveRef.current) return;
      setSaving(false);
      if (!result) {
        setAutoError("Sua sessão expirou. Entre novamente para continuar.");
        return;
      }
      if (!result.ok) {
        setAutoError(autoTopupFailureCopy(result.failure.code, result.failure.reason));
      }
      refresh();
    },
    [refresh],
  );

  const snapshot = balance.snapshot;
  if (!snapshot) return null;
  if (!hasNegotiationAccount(snapshot)) {
    return (
      <section className="negotiationBalancePanel" data-testid="negotiation-balance-opening">
        <header className="negotiationBalancePanelHeader">
          <span className="negotiationBalancePanelIcon" aria-hidden="true">
            <AccountBalanceWalletOutlinedIcon fontSize="small" />
          </span>
          <div>
            <h2 className="negotiationBalancePanelTitle">Saldo de Negociações</h2>
            <p className="negotiationBalancePanelSubtitle">
              Pré-pago, usado só nos lances do Átrio
            </p>
          </div>
        </header>
        <p className="negotiationBalanceText">
          Abra o saldo pré-pago da empresa para participar dos leilões do Átrio. Basta o perfil de
          faturamento com CNPJ e razão social. Não é preciso autorizar o Pix Automático para abrir o
          saldo ou dar lances. A veiculação no Átrio exige Pix Automático aprovado e cobrança-base
          semanal admitida.
        </p>
        {canCreate ? (
          <Button callback={() => setTermsOpen(true)}>Aceitar condições e abrir saldo</Button>
        ) : (
          <p className="negotiationBalanceHint">
            Peça a alguém com permissão de Pagamentos para aceitar as condições e abrir o saldo.
          </p>
        )}
        {termsOpen && (
          <TermsAcceptanceFlow
            version={snapshot.terms.version}
            canAccept={canCreate}
            onBalanceChanged={refresh}
            onClose={() => setTermsOpen(false)}
          />
        )}
      </section>
    );
  }
  const canRecharge = canCreate && snapshot.account.state !== "closed";
  // The automatic top-up is a top-up: it turns on only after the accepted
  // conditions, and only for a version /termos publishes.
  const termsGate: AutoTopupTermsGate = !isNegotiationBalanceTermsPublished(snapshot.terms.version)
    ? "in_review"
    : snapshot.terms.required
      ? "required"
      : "accepted";
  return (
    <>
      <NegotiationBalancePanelView
        snapshot={snapshot}
        canRecharge={canRecharge}
        stale={balance.stale}
        nowMs={Date.now()}
        onRecharge={() => setRecharge({})}
        onResumeTopup={(resumeTopupId) => setRecharge({ resumeTopupId })}
        autoTopup={
          snapshot.autoTopup.available && canEdit ? (
            <AutoTopupSettings
              autoTopup={snapshot.autoTopup}
              terms={termsGate}
              onAcceptTerms={() => setTermsOpen(true)}
              minAmountCents={snapshot.limits.minTopupCents}
              saving={saving}
              error={autoError}
              onSave={(input) => void saveAutoTopup(input)}
            />
          ) : undefined
        }
        history={onHistoryKeyChange ? undefined : <BalanceHistory reloadKey={snapshot.revision} />}
      />
      {termsOpen && (
        <TermsAcceptanceFlow
          version={snapshot.terms.version}
          canAccept={canCreate}
          onBalanceChanged={refresh}
          onClose={() => setTermsOpen(false)}
        />
      )}
      {recharge && (
        <RechargeFlow
          origin="payments_tab"
          resumeTopupId={recharge.resumeTopupId}
          balance={snapshot}
          canAcceptTerms={canCreate}
          onBalanceChanged={refresh}
          onClose={() => {
            setRecharge(null);
            refresh();
          }}
        />
      )}
    </>
  );
}
